import type { ReactNode } from 'react';
import React, { Fragment, useEffect, useState, useContext, useCallback } from 'react';
import { styled } from '@compiled/react';

import { EditorPreloadManager } from '@confluence/editor-preloader/entry-points/EditorPreloadManager.ts';
import { useEditSource } from '@confluence/edit-common/entry-points/useEditSource.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { EditContentButtonPreloaderContext } from './EditContentButtonPreloaderContext.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Styling div for hover area only
const HoverAreaWrapper = styled.div({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Pseudo-element to expand hover area
	'&::before': {
		content: '""',
		position: 'absolute',
		inset: '-8px',
	},
});

export type EditContentButtonPreloaderProps = {
	contentId: string;
	spaceKey?: string;
	isEmbeddedEditor?: boolean;
	children: ReactNode;
	editSource: string;
};

export const EditContentButtonPreloader = ({
	contentId,
	spaceKey,
	children,
	editSource,
	isEmbeddedEditor,
}: EditContentButtonPreloaderProps) => {
	const preloader = useContext(EditContentButtonPreloaderContext); // See packages/confluence-fabric-editor/src/editorPreloader.tsx
	const [doPreload, setDoPreload] = useState(false);

	const [{ editSource: contextEditSource }, { setEditSource }] = useEditSource();

	// Cleanup editorPreloadManager on contentId change and unmount.
	useEffect(() => {
		return () => {
			if (doPreload) {
				EditorPreloadManager.removePreloadedId(contentId);
			}
		};
	}, [doPreload, contentId]);

	const onHover = useCallback(async () => {
		if (EditorPreloadManager.shouldPreload(contentId)) {
			setDoPreload(true);
			setEditSource(editSource);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, editSource]);

	return (
		<Fragment>
			{expValEquals('platform_editor_collabdraft_query_improvements', 'isEnabled', true) ? (
				<HoverAreaWrapper onMouseEnter={onHover}>{children}</HoverAreaWrapper>
			) : (
				/* eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events */
				<div onMouseEnter={onHover}>{children}</div>
			)}
			{editSource === contextEditSource && doPreload && preloader
				? preloader({
						contentId,
						spaceKey,
						isEmbeddedEditor,
					})
				: null}
		</Fragment>
	);
};
